import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star, Loader2, CheckCircle2, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | form | submitted | already | invalid
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/feedback/${token}`);
        setInfo(res.data);
        setState(res.data.already_submitted ? 'already' : 'form');
      } catch {
        setState('invalid');
      }
    };
    fetchInvite();
  }, [token]);

  const submit = async () => {
    if (!rating) {
      toast.error('Selecciona una valoración de 1 a 5 estrellas');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/feedback/${token}`, { rating, comment: comment.trim() });
      setState('submitted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar tu opinión');
    } finally {
      setSending(false);
    }
  };

  const labels = ['', 'Muy mejorable', 'Mejorable', 'Correcta', 'Buena', 'Excelente'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/60 to-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <h1 className="font-heading text-2xl font-bold">A Gift of Memories</h1>
          <p className="text-sm text-muted-foreground">Tu opinión nos ayuda a mejorar</p>
        </div>

        {state === 'loading' && (
          <div className="flex justify-center py-16" data-testid="feedback-loading">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {state === 'invalid' && (
          <Card className="card-elevated" data-testid="feedback-invalid">
            <CardContent className="py-10 text-center text-muted-foreground">
              Este enlace no es válido o ha caducado.
            </CardContent>
          </Card>
        )}

        {state === 'already' && (
          <Card className="card-elevated" data-testid="feedback-already">
            <CardContent className="py-10 text-center space-y-2">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="font-medium">Ya nos enviaste tu opinión</p>
              <p className="text-sm text-muted-foreground">¡Muchas gracias por ayudarnos a mejorar!</p>
            </CardContent>
          </Card>
        )}

        {state === 'submitted' && (
          <Card className="card-elevated" data-testid="feedback-thanks">
            <CardContent className="py-10 text-center space-y-3">
              <Heart className="w-10 h-10 text-rose-500 mx-auto" />
              <p className="font-heading text-xl font-semibold">¡Gracias de corazón!</p>
              <p className="text-sm text-muted-foreground">
                Tu opinión nos ayuda a que cada libro emocione un poco más.
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'form' && (
          <Card className="card-elevated" data-testid="feedback-form">
            <CardHeader>
              <CardTitle className="font-heading text-xl">
                {info?.name ? `Hola ${info.name},` : 'Hola,'} ¿qué te ha parecido?
              </CardTitle>
              <CardDescription>
                Cuéntanos cómo ha sido tu experiencia con A Gift of Memories y qué podríamos mejorar. Solo te llevará un minuto.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="text-center">
                <div className="flex justify-center gap-2" data-testid="feedback-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHovered(n)}
                      onMouseLeave={() => setHovered(0)}
                      onClick={() => setRating(n)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`feedback-star-${n}`}
                    >
                      <Star
                        className={`w-9 h-9 ${(hovered || rating) >= n ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1 h-4">{labels[hovered || rating]}</p>
              </div>
              <div>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="¿Qué te ha gustado? ¿Qué podríamos mejorar? (opcional)"
                  className="min-h-[110px]"
                  data-testid="feedback-comment"
                />
              </div>
              <Button
                onClick={submit}
                disabled={sending || !rating}
                className="w-full btn-primary gap-2"
                data-testid="feedback-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
                Enviar mi opinión
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
